import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import * as Dialog from '@radix-ui/react-dialog';
import { dialogMotion } from '../utils/motion';
import { clean, stripHtml } from '../utils/quinn';
import { useIsEditing } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

interface Offer {
  title?: string;
  description?: string;
  disclaimer?: string;
  end_date?: string;
  enabled?: string | boolean;
}

const tourCtaDef: SlotGroupDefinition = {
  label: 'Offer Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Offer Contact Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const offerActions = [
  'relative flex shrink-0 items-center gap-6',
  '[&_.btn-primary]:bg-[--q-on-offer] [&_.btn-primary]:text-[--q-offer] [&_.btn-primary]:[border-color:transparent] [&_.btn-primary:hover]:bg-[--q-on-offer]',
  'max-[767px]:gap-1',
  'max-[767px]:[&_.btn]:min-h-11 max-[767px]:[&_.btn]:w-auto max-[767px]:[&_.btn]:rounded-none max-[767px]:[&_.btn]:p-0',
  'max-[767px]:[&_.btn]:text-[13px] max-[767px]:[&_.btn]:text-[--q-on-offer] max-[767px]:[&_.btn]:underline max-[767px]:[&_.btn]:underline-offset-[3px]',
  'max-[767px]:[&_.btn]:[background:none] max-[767px]:[&_.btn]:[border:0] max-[767px]:[&_.btn]:[box-shadow:none] max-[767px]:[&_.btn]:[transform:none]',
  'max-[767px]:[&_.btn:hover]:[background:none] max-[767px]:[&_.btn:hover]:[box-shadow:none] max-[767px]:[&_.btn:hover]:[transform:none]',
  'max-[767px]:[&_.btn:active]:[box-shadow:none] max-[767px]:[&_.btn:active]:[transform:none]',
].join(' ');

const cleanText = (v: unknown) => stripHtml(clean(v) || '').replace(/\*/g, '').trim();
const endsOf = (o: Offer) => {
  const raw = clean(o.end_date);
  const d = raw ? new Date(raw.length <= 10 ? `${raw}T12:00:00` : raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};

export function SpecialOfferBanner() {
  const editing = useIsEditing();
  const flags = useSiteFlags();
  const offers = useCollectionBinding<Offer>('specialofferbanner.offers', {
    collection: 'special_offers',
    defaultLimit: 1,
    filter: { enabled: 'true' },
  });
  const fallbackTitle = useContentToken('specialofferbanner.fallback_title') || 'Limited-time rent special';
  const tour = useSlotGroup('specialofferbanner.tour_cta', tourCtaDef);
  const contact = useSlotGroup('specialofferbanner.contact_cta', contactCtaDef);

  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bannerGone, setBannerGone] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const offer = offers[0] || null;

  /** Other sections (the floor plans hero's offer pill) open the modal with a `quinn:open-offer` window event. */
  useEffect(() => {
    if (!offer) return;
    const open = () => setModalOpen(true);
    window.addEventListener('quinn:open-offer', open);
    return () => window.removeEventListener('quinn:open-offer', open);
  }, [offer]);

  useEffect(() => {
    if (!offer) return;
    const onScroll = () => setBannerGone(!bannerRef.current || bannerRef.current.getBoundingClientRect().bottom <= 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [offer, dismissed]);

  if (!offer) {
    if (!editing) return null;
    return (
      <EditableCollectionBinding bindingPath="specialofferbanner.offers" label="Special Offers">
        <div className="offer-banner offer-banner--empty">
          <div className="eyebrow eyebrow--on-offer">Special offer banner · shows when an offer is enabled in MultiHub</div>
        </div>
      </EditableCollectionBinding>
    );
  }

  const ownTitle = clean(offer.title) && offer.title !== 'Rent Specials' ? cleanText(offer.title).replace(/!$/, '') : null;
  const title = ownTitle || fallbackTitle;
  const desc = cleanText(offer.description);
  const first = desc.split(/(?<=[.!?])\s/)[0] || '';
  const rest = desc.slice(first.length).trim();
  const disclaimer = cleanText(offer.disclaimer);
  const ends = endsOf(offer);
  const tourLink = tourCta(tour, flags.selfTourUrl);

  return (
    <>
      {!dismissed && (
        <EditableCollectionBinding bindingPath="specialofferbanner.offers" label="Special Offers">
          <div className="offer-banner" ref={bannerRef}>
            <div
              className="pointer-events-none absolute left-[-8px] top-[-44px] font-heading text-[180px] font-light italic leading-none opacity-[.09] max-[767px]:hidden"
              aria-hidden="true"
            >
              Q
            </div>
            <div className="relative flex min-w-0 flex-auto items-center gap-8">
              <div className="flex min-w-0 flex-auto flex-col gap-1 max-[767px]:[&_.eyebrow]:overflow-hidden max-[767px]:[&_.eyebrow]:text-ellipsis max-[767px]:[&_.eyebrow]:whitespace-nowrap max-[767px]:[&_.eyebrow]:text-[11px] max-[767px]:[&_.eyebrow]:leading-4 max-[767px]:[&_.eyebrow]:tracking-[.06em]">
                <div className="eyebrow eyebrow--on-offer">
                  {ownTitle || (
                    <RichTextContent path="specialofferbanner.fallback_title" as="span" />
                  )}
                  {ends ? ` · ends ${ends}` : ''}
                </div>
                <div className="line-clamp-2 text-balance font-heading text-[24px] leading-[30px] max-[767px]:hidden [&_em]:font-light [&_em]:italic">
                  {first.replace(/!$/, '')}
                </div>
              </div>
              {(rest || disclaimer) && (
                <div className="line-clamp-3 min-w-[240px] flex-[0_1_400px] border-l border-l-[color-mix(in_srgb,var(--q-ink)_20%,transparent)] pl-8 text-[13px] leading-[18px] opacity-[.85] empty:hidden max-[767px]:hidden">
                  {rest || disclaimer}
                </div>
              )}
            </div>
            <div className={offerActions}>
              <button className="btn btn-primary btn--sm" type="button" onClick={() => setModalOpen(true)}>
                <RichTextContent path="specialofferbanner.cta_label" as="span" className="max-[767px]:hidden" />
                <RichTextContent path="specialofferbanner.cta_short_label" as="span" className="hidden max-[767px]:inline" />
              </button>
              <button className="h-11 w-11 text-[22px] leading-none opacity-90" type="button" aria-label="Dismiss offer" onClick={() => setDismissed(true)}>
                ×
              </button>
            </div>
          </div>
        </EditableCollectionBinding>
      )}

      {(dismissed || bannerGone) && (
        <button
          className="fixed right-4 top-[calc(var(--header-utility)+var(--header-main)+12px)] z-[39] inline-flex min-h-10 items-center gap-2 rounded-full bg-[--q-offer] py-0 pl-3 pr-4 text-[12px] font-medium uppercase tracking-[.06em] text-[--q-on-offer] [box-shadow:0_8px_24px_color-mix(in_srgb,var(--q-ink)_18%,transparent)] [transition:transform_var(--q-motion-fast)] hover:[transform:translateY(-1px)] max-[767px]:min-h-11 max-[767px]:w-11 max-[767px]:justify-center max-[767px]:p-0"
          type="button"
          aria-label="Special offer"
          onClick={() => setModalOpen(true)}
        >
          <span className="text-[14px]" aria-hidden="true">★</span>
          <RichTextContent path="specialofferbanner.pill_label" as="span" className="max-[767px]:hidden" />
        </button>
      )}

      <Dialog.Root open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className={`fixed inset-0 z-[82] flex items-center justify-center bg-[color-mix(in_srgb,var(--q-ink)_55%,transparent)] p-4 ${dialogMotion.fade}`}>
            <Dialog.Content className={`relative max-h-[90vh] w-[min(560px,100%)] overflow-auto rounded-lg border-t-8 border-t-[--q-offer] bg-[--q-paper] px-12 pb-10 pt-12 text-[--q-ink] outline-none max-[767px]:px-6 max-[767px]:pb-8 max-[767px]:pt-10 ${dialogMotion.pop}`}>
              <Dialog.Close
                className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-[24px] text-[--q-muted] hover:bg-[--q-surface] hover:text-[--q-ink]"
                aria-label="Close"
              >
                ×
              </Dialog.Close>
              <div className="eyebrow">Special offer{ends ? ` · ends ${ends}` : ''}</div>
              <Dialog.Title asChild>
                <h3 className="mb-0 mt-3 font-heading text-[34px] font-light leading-10 tracking-[--q-tracking-tight] max-[767px]:text-[28px] max-[767px]:leading-[34px]">{title}</h3>
              </Dialog.Title>
              <Dialog.Description asChild>
                <p className="mb-0 mt-4 text-[16px] leading-[26px]">{desc}</p>
              </Dialog.Description>
              {disclaimer && <p className="mb-0 mt-3 text-[13px] leading-[19px] text-[--q-muted]">{disclaimer}</p>}
              <div className="mt-7 flex flex-wrap gap-3">
                <EditableSlotGroup groupPath="specialofferbanner.tour_cta" definition={tourCtaDef}>
                  <a
                    className="btn btn-primary"
                    href={tourLink.href}
                    target={tourLink.isTour ? tour._target || '_self' : '_self'}
                    data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
                  >
                    {tourLink.label}
                  </a>
                </EditableSlotGroup>
                {tourLink.isTour && (
                  <EditableSlotGroup groupPath="specialofferbanner.contact_cta" definition={contactCtaDef}>
                    <a className="btn btn-secondary" href={contact.url || PATHS.contact} target={contact._target || '_self'}>
                      {contact.label || 'Contact Us'}
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOfferBanner',
  displayName: 'Special Offer Banner',
  tokens: {
    'specialofferbanner.fallback_title': {
      label: 'Offer Label (when the offer has no title)',
      type: 'richtext',
      defaultValue: '<p>Limited-time rent special</p>',
    },
    'specialofferbanner.cta_label': { label: 'Button Text', type: 'richtext', defaultValue: '<p>Claim This Offer</p>' },
    'specialofferbanner.cta_short_label': { label: 'Button Text (phone)', type: 'richtext', defaultValue: '<p>View Offer</p>' },
    'specialofferbanner.pill_label': { label: 'Floating Pill Text', type: 'richtext', defaultValue: '<p>Special Offer</p>' },
  },
  slotGroups: {
    'specialofferbanner.tour_cta': tourCtaDef,
    'specialofferbanner.contact_cta': contactCtaDef,
  },
  collectionBindings: {
    'specialofferbanner.offers': {
      label: 'Special Offers',
      collection: 'special_offers',
      defaultLimit: 1,
      filter: { enabled: 'true' },
    },
  },
};

(SpecialOfferBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOfferBanner;
